/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-07-10 22:06:42
 */
import React, { useEffect, useState } from 'react'
import { Button, Checkbox } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import CONFIG from '../../constants/config'
import DatesPicker from '../components/DatesPickerV2'
import { initialState, useAIPhoto, useAIPhotoDispatch } from './hooks/Context'
import { getPhotoLabels, getPhotoList } from './api'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import sensors, { sensorsRole } from '../../utils/sensors'
import ai_photo_menu_icon from '../../images/ai_photo_menu_icon.svg?url'

// 类型定义
interface LabelItem {
  labels: string[]
  showLabelName: string
}

interface QuickPickerItem {
  text: string
  clickItem: string
  startTime: () => Dayjs
  endTime: () => Dayjs
  width?: string
}

interface SensorClickHandler {
  (clickItem: string, restProperties?: Record<string, any>): void
}

const quickPickerItems: QuickPickerItem[] = [
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day')
  },
  {
    text: '上周',
    clickItem: 'lastWeek',
    startTime: () => dayjs().add(-1, 'weeks').startOf('weeks'),
    endTime: () => dayjs().add(-1, 'weeks').endOf('weeks'),
    width: 'auto'
  },
  {
    text: '过去14天',
    clickItem: 'past=14days',
    startTime: () => dayjs().add(-14, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto'
  }
]

const quickPickerItemsInDatePicker: QuickPickerItem[] = [
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day')
  },
  {
    text: '上周',
    clickItem: 'lastWeek',
    startTime: () => dayjs().add(-1, 'weeks').startOf('weeks'),
    endTime: () => dayjs().add(-1, 'weeks').endOf('weeks'),
    width: 'auto'
  },
  {
    text: '过去7天',
    clickItem: 'past7days',
    startTime: () => dayjs().add(-7, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto'
  },
  {
    text: '过去14天',
    clickItem: 'past14days',
    startTime: () => dayjs().add(-14, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto'
  }
]

interface TimePickerAreaProps {
  onSensorClick: SensorClickHandler
}

const TimePickerArea = (props: TimePickerAreaProps) => {
  const { onSensorClick } = props
  const { filter } = useAIPhoto()
  const dispatch = useAIPhotoDispatch()
  const [datePickerPaneVisible, setDatePickerPaneVisible] = useState(false)

  const startTime = filter.times[0]
  const endTime = filter.times[1]
  const isThisMonth =
    dayjs().startOf('month').isSame(startTime, 'second') &&
    dayjs().endOf('day').isSame(endTime, 'second')
  const isAllPhoto =
    startTime.isSame(dayjs('1970-01-01')) &&
    endTime.isSame(dayjs().endOf('days'))
  const startTimeFormated = dayjs().isSame(startTime, 'year')
    ? startTime.format('M月D日')
    : startTime.format('YY年M月D日')
  const endTimeFormated = dayjs().isSame(endTime, 'year')
    ? endTime.format('M月D日')
    : endTime.format('YY年M月D日')

  const handleClick = () => {
    setDatePickerPaneVisible(true)
  }

  const handleDateChange = (startTime: Dayjs, endTime: Dayjs) => {
    dispatch({
      type: 'setFilter',
      data: {
        ...filter,
        times: [startTime, endTime]
      }
    })
    setDatePickerPaneVisible(false)
  }

  return (
    <div className="time-wrapper">
      <p className="label">筛选时间</p>
      <p className="timepicker-wrapper" onClick={handleClick}>
        {isThisMonth && <span className="this-month">本月 丨 </span>}
        {isAllPhoto ? (
          <span className="this-month">所有照片</span>
        ) : (
          startTimeFormated + '-' + endTimeFormated
        )}
        <CalendarOutlined />
      </p>
      <p className="quickpicker-wrapper">
        快速筛选{' '}
        {quickPickerItems.map((item, index) => {
          return (
            <Button
              type="link"
              size="small"
              onClick={() => {
                handleDateChange(item.startTime(), item.endTime())
                onSensorClick('quickChooseTime', {
                  clickQuickTimeContent: item.text
                })
              }}
              key={index}
            >
              {item.text}
            </Button>
          )
        })}
      </p>
      <DatesPicker
        maxMonths={1}
        quickPickerItems={quickPickerItemsInDatePicker}
        disabledDate={(current: Dayjs) => {
          return (
            current.isAfter(dayjs(), 'day') ||
            current.isBefore(dayjs().add(-14, 'day'), 'day') ||
            current.isSame(dayjs(), 'day')
          )
        }}
        visible={datePickerPaneVisible}
        dates={filter.times}
        onChange={handleDateChange}
        onCancel={() => setDatePickerPaneVisible(false)}
      />
    </div>
  )
}

interface LabelsAreaProps {
  onSensorClick: SensorClickHandler
}

const LabelsArea = (props: LabelsAreaProps) => {
  const { onSensorClick } = props
  const appDispatch = useAppDispatch()
  const dispatch = useAIPhotoDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const [labels, setLabels] = useState<LabelItem[]>([])
  const { filter } = useAIPhoto()
  const selectedLables = filter.labels

  const fetchLabels = () => {
    appDispatch(
      getPhotoLabels({
        groupID
      })
    ).then((res: any) => {
      setLabels(res.showLabels)
    })
  }

  const handleSelectLabel = (
    checked: boolean,
    checkedLables: string[],
    showLabelName: string
  ) => {
    if (checked) {
      onSensorClick('checkAILabel', { labelName: showLabelName })
      dispatch({
        type: 'setFilter',
        data: { ...filter, labels: [...filter.labels, ...checkedLables] }
      })
    } else {
      onSensorClick('uncheckAILabel', { labelName: showLabelName })
      dispatch({
        type: 'setFilter',
        data: {
          ...filter,
          labels: filter.labels.filter((item) => !checkedLables.includes(item))
        }
      })
    }
  }

  useEffect(() => {
    fetchLabels()
  }, [groupID])

  return (
    <div className="labels-wrapper">
      <p className="label-title">
        <img src={ai_photo_menu_icon} alt="" />
        AI 智能识别场景
      </p>
      <p className="labels">
        {labels.map((item, index) => {
          const isSelected = item.labels.every((label) =>
            selectedLables.includes(label)
          )
          return (
            <Checkbox
              key={index}
              checked={isSelected}
              onChange={(e) =>
                handleSelectLabel(
                  e.target.checked,
                  item.labels,
                  item.showLabelName
                )
              }
            >
              <span className="label-test">{item.showLabelName}</span>
            </Checkbox>
          )
        })}
      </p>
    </div>
  )
}

const Filter = () => {
  const appDispatch = useAppDispatch()
  const dispatch = useAIPhotoDispatch()
  const { filter } = useAIPhoto()
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)

  const handleSensorOfClickEvent: SensorClickHandler = (
    clickItem,
    restProperties = {}
  ) => {
    sensors.track('web_workgroup_ai_label_photo_click', {
      clickItem,
      groupID,
      role: sensorsRole(myRole),
      ...restProperties
    })
  }

  const haneleFetchPhotos = () => {
    appDispatch(
      getPhotoList({
        filter: {
          times: [
            {
              start: filter.times[0].format(CONFIG.timeFormat),
              end: filter.times[1].format(CONFIG.timeFormat)
            }
          ],
          labels: filter.labels
        },
        groupID,
        lastPageCond: ''
      })
    ).then((res: any) => {
      dispatch({ type: 'setPhotos', data: res.photos })
      dispatch({ type: 'setLastPageCond', data: res.lastPageCond })
    })
  }

  const handleReset = () => {
    dispatch({ type: 'setFilter', data: initialState.filter })
    handleSensorOfClickEvent('reset')
  }

  useEffect(() => {
    haneleFetchPhotos()
  }, [filter])

  return (
    <div
      className="photo-filter-wrapper"
      style={{ display: 'flex', alignItems: 'flex-end' }}
    >
      <div className="gradient-border">
        <div className="time-and-keywords-wrapper labels-and-time-wrapper">
          <LabelsArea onSensorClick={handleSensorOfClickEvent} />
          <TimePickerArea onSensorClick={handleSensorOfClickEvent} />
        </div>
      </div>

      <Button size="large" style={{ marginLeft: '20px' }} onClick={handleReset}>
        重置
      </Button>
    </div>
  )
}

export default Filter
